import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import CollectionViewModel from "../viewmodels/CollectionViewModel";
import CounterViewModel from "../viewmodels/CounterViewModel";
import PhotoGallery from "./PhotoGallery";

// One collection: its curated images, fetched on open, shown with the exact same
// experience as the gallery (pagination, spinner, quotes, lightbox).
const CollectionDetailView = () => {
  const { id, page } = useParams();
  const parsedPage = parseInt(page, 10);
  const currentPage = Number.isInteger(parsedPage) && parsedPage >= 1 ? parsedPage : 1;

  const [collection, setCollection] = useState(undefined); // undefined=loading, null=not found
  const [imageKeys, setImageKeys] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setImageKeys(null);
    (async () => {
      try {
        const [col, counter] = await Promise.all([
          CollectionViewModel.getCollection(id),
          CounterViewModel.getCounter(),
        ]);
        if (cancelled) return;
        if (!col) {
          setCollection(null);
          return;
        }
        setCollection(col);
        setImageKeys(CollectionViewModel.publishedImages(col.images, counter));
      } catch (e) {
        if (!cancelled) {
          setCollection(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (collection === null) {
    return (
      <Missing>
        <p>That collection could not be found.</p>
        <BackLink to="/collections">All collections</BackLink>
      </Missing>
    );
  }

  return (
    <>
      {collection && (
        <Header>
          <BackLink to="/collections">← Collections</BackLink>
          <Title>{collection.name}</Title>
          {collection.description && <Desc>{collection.description}</Desc>}
        </Header>
      )}
      <PhotoGallery imageKeys={imageKeys} currentPage={currentPage} basePath={`/collections/${id}`} />
    </>
  );
};

export default CollectionDetailView;

const Header = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 28px var(--gap) 4px;
  box-sizing: border-box;
  width: 100%;
`;

const Title = styled.h1`
  margin: 10px 0 8px;
  color: var(--paper);
  font-size: clamp(24px, 3vw, 36px);
`;

const Desc = styled.p`
  margin: 0;
  color: var(--muted);
  max-width: 70ch;
  line-height: 1.6;
`;

const BackLink = styled(Link)`
  color: var(--muted);
  text-decoration: none;
  font-size: 14px;
  transition: color var(--dur-fast) ease;
  &:hover {
    color: var(--accent);
  }
`;

const Missing = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 80px 20px;
  color: var(--muted);
`;
